/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import {
  BuilderPermission,
  DatesFilter,
  JobListFilterDto,
  JobRestService,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import { Enum, SlicedDataSource } from '@gh/core-data';
import { GridColumn, GridLayoutService } from '@gh/core-ui-grid';

import { JobListItem } from './job.model';
import { BuilderUserContext, FilterNames, FilterRestService } from '@gh/builder-shared';
import { ENV_BASE_URL } from '@gh/config';
import { Observable } from 'rxjs';
import { padStart } from 'lodash';
import { toString } from '@gh/core-util';
import { ID } from '@gh/common-shared-data';

@Injectable()
export class JobListBusinessService implements InitViewService {

  COLUMN_ENABLE_CONDITION = {
    estimatedProfit: (): boolean => this.getPermissionForPrices(),
    totalAmount: (): boolean => this.getCountry() !== 'us',
    sitePostcode: (): boolean => this.getCountry() !== 'nz',
    profitMargin: (): boolean => this.getPermissionForPrices(),
    originalBudget: (): boolean => this.getPermissionForPrices(),
    originalContractAmount: (): boolean => this.getPermissionForPricesExtended(),
    originalProfitMargin: (): boolean => this.getPermissionForPrices(),
    currentBudget: (): boolean => this.getPermissionForPrices(),
    totalInvoices: (): boolean => this.getPermissionForPricesExtended(),
    drawsMadeWithGst: (): boolean => this.getPermissionForPricesExtended(),
    actualProfit: (): boolean => this.getPermissionForPrices(),
    estimateCost: (): boolean => this.hasCostPermission(),
  };

  constructor(private jobRest: JobRestService,
              private userContext: BuilderUserContext,
              private dictionaries: DictionaryService,
              private filterService: FilterRestService,
              private gridLayoutService: GridLayoutService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      gridLayout: this.gridLayoutService.get('jobs', this.COLUMN_ENABLE_CONDITION),
      datesFilter: this.dictionaries.getByEnum(DatesFilter),
      jobFilters:  this.dictionaries.get('JOBS_FILTERS'),
      filter:  this.filterService.get(FilterNames.jobs),
    };
  }

  findAllAsDataSource(filter: JobListFilterDto): SlicedDataSource<JobListItem> {
    return this.jobRest.findAllAsDataSource(filter);
  }

  getCountry(): string {
    const baseUrl = ENV_BASE_URL.toString();
    // @ts-ignore
    switch (true) {
      case baseUrl.includes('-au') || baseUrl.includes('.au'):
        return 'au';
      case baseUrl.includes('-nz') || baseUrl.includes('.nz'):
        return 'nz';
      case baseUrl.includes('-us') || baseUrl.includes('.com'):
        return 'us';
      default:
        return 'us';
    }
  }

  getPermissionForPrices(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT)
      || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.VIEW)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.Financials.VIEW);
  }

  getPermissionForPricesExtended(): boolean {
    return this.getPermissionForPrices()
    || this.userContext.hasPermission(BuilderPermission.Accounting.Draws.VIEW)
    || this.userContext.hasPermission(BuilderPermission.Accounting.Draws.PRODUCE);
  }

  hasCostPermission(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
     || this.userContext.hasSystemRole(SystemRoleBit.Master)
     || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.EDIT)
     || this.userContext.hasPermission(BuilderPermission.Estimating.JobEstimate.VIEW);
  }

  export(filter: JobListFilterDto, columns: GridColumn[]): Observable<{url: string; fileName: string}> {
    if (filter.dateFrom) {
      const dateFrom = new Date(filter.dateFrom);
      filter.dateFrom = `${[dateFrom.getFullYear(), dateFrom.getMonth() + 1, dateFrom.getDate()].map(this.stringifyDateComponent).join('-')}`;
    }
    if (filter.dateTo) {
      const dateTo = new Date(filter.dateTo);
      filter.dateTo = `${[dateTo.getFullYear(), dateTo.getMonth() + 1, dateTo.getDate()].map(this.stringifyDateComponent).join('-')}`;
    }
    if (filter.dateType) {
      filter.dateType = Enum.toValue(DatesFilter, filter.dateType);
    }
    return this.jobRest.exportJobsList(filter, columns);
  }

  recalculate(): Observable<any> {
    return this.jobRest.recalculateJobList();
  }

  stringifyDateComponent(n: number): string {
    return padStart(toString(n), 2, '0'); // tslint:disable-line:no-magic-numbers
  }

  validate(jobId: ID): Observable<any> {
    return this.jobRest.validateJob(jobId);
  }
}
